import { useState, useEffect } from "react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { FileText, Check, ChevronDown, ChevronUp } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function AthleteClaimBanner() {
  const [claimable, setClaimable] = useState({ name_matches: [], available_reports: [] });
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [claiming, setClaiming] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const fetchClaimable = async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await fetch(`${API_URL}/api/athlete/claimable-reports`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          setClaimable(data);
        }
      } catch (err) {
        console.warn("Could not check claimable reports");
      } finally {
        setLoading(false);
      }
    };
    fetchClaimable();
  }, []);

  const claimReport = async (athleteId) => {
    setClaiming(athleteId);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API_URL}/api/athlete/claim-report/${athleteId}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        // Remove from list
        setClaimable((prev) => ({
          name_matches: prev.name_matches.filter((p) => p.athlete_id !== athleteId),
          available_reports: prev.available_reports.filter((p) => p.athlete_id !== athleteId),
        }));
      } else {
        const err = await res.json();
        toast.error(err.detail || "Claim failed");
      }
    } catch (err) {
      toast.error("Claim failed");
    } finally {
      setClaiming(null);
    }
  };

  if (loading || dismissed) return null;

  const matches = claimable.name_matches || [];
  const available = claimable.available_reports || [];
  const total = [...new Map([...matches, ...available].map((p) => [p.athlete_id, p])).values()];

  if (total.length === 0) return null;

  return (
    <div className="bg-gradient-to-r from-blue-900 to-blue-800 text-white rounded-xl p-4 mb-6 shadow-lg" data-testid="claim-banner">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
            <FileText className="h-5 w-5" />
          </div>
          <div>
            <h3 className="font-bold text-base">
              {matches.length > 0
                ? "Your Scouting Report is Ready!"
                : `${total.length} Scouting Report${total.length > 1 ? "s" : ""} Available to Claim`}
            </h3>
            <p className="text-blue-200 text-sm">
              {matches.length > 0
                ? "We found a report that matches your name. Claim it to add it to your profile."
                : "Browse available scouting reports and claim yours."}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="ghost"
            className="text-white hover:bg-white/20"
            onClick={() => setExpanded(!expanded)}
          >
            {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            {expanded ? "Hide" : "View"}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="text-blue-300 hover:text-white hover:bg-white/10 text-xs"
            onClick={() => setDismissed(true)}
          >
            Dismiss
          </Button>
        </div>
      </div>

      {expanded && (
        <div className="mt-4 space-y-2">
          {total.map((p) => {
            const isMatch = matches.some((m) => m.athlete_id === p.athlete_id);
            return (
              <div
                key={p.athlete_id}
                className={`flex items-center justify-between p-3 rounded-lg ${
                  isMatch ? "bg-white/20 border border-white/30" : "bg-white/10"
                }`}
              >
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-semibold">{p.name}</span>
                    {isMatch && (
                      <span className="text-xs bg-green-500 text-white px-2 py-0.5 rounded-full">
                        Name Match
                      </span>
                    )}
                  </div>
                  <div className="text-sm text-blue-200">
                    {p.school || "Unknown School"} · {p.position_primary || p.position || "--"}
                    {p.scouting_report_filename && ` · ${p.scouting_report_filename}`}
                  </div>
                </div>
                <Button
                  size="sm"
                  onClick={() => claimReport(p.athlete_id)}
                  disabled={claiming === p.athlete_id}
                  className="bg-white text-blue-900 hover:bg-blue-50 font-semibold"
                  data-testid={`claim-btn-${p.athlete_id}`}
                >
                  {claiming === p.athlete_id ? (
                    "Claiming..."
                  ) : (
                    <>
                      <Check className="h-4 w-4 mr-1" /> Claim
                    </>
                  )}
                </Button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function CoachClaimBanner() {
  const [claimData, setClaimData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [claiming, setClaiming] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const fetchClaimable = async () => {
      try {
        const token = localStorage.getItem("token");
        const res = await fetch(`${API_URL}/api/coach/claimable-profile`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          setClaimData(await res.json());
        }
      } catch (err) {
        console.warn("Could not check coach claim");
      } finally {
        setLoading(false);
      }
    };
    fetchClaimable();
  }, []);

  const claimProfile = async (coachId) => {
    setClaiming(true);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API_URL}/api/coach/claim-profile/${coachId}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        setDismissed(true);
        // Reload to update role
        window.location.reload();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Claim failed");
      }
    } catch (err) {
      toast.error("Claim failed");
    } finally {
      setClaiming(false);
    }
  };

  if (loading || dismissed) return null;

  const exact = claimData?.exact_match;
  const similar = claimData?.similar_coaches || [];

  if (!exact && similar.length === 0) return null;

  return (
    <div className="bg-gradient-to-r from-red-700 to-red-600 text-white rounded-xl p-4 mb-6 shadow-lg" data-testid="coach-claim-banner">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center text-xl font-bold">
            C
          </div>
          <div>
            {exact ? (
              <>
                <h3 className="font-bold text-base">Welcome, Coach {exact.name}!</h3>
                <p className="text-red-200 text-sm">
                  {exact.school} · {exact.division} · {exact.position}
                </p>
              </>
            ) : (
              <>
                <h3 className="font-bold text-base">Coach Profiles Available at Your School</h3>
                <p className="text-red-200 text-sm">
                  We found {similar.length} unclaimed coach profile{similar.length > 1 ? "s" : ""} at your institution.
                </p>
              </>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {exact && (
            <Button
              size="sm"
              onClick={() => claimProfile(exact.coach_id)}
              disabled={claiming}
              className="bg-white text-red-700 hover:bg-red-50 font-semibold"
              data-testid="claim-coach-btn"
            >
              {claiming ? "Claiming..." : "Claim My Profile"}
            </Button>
          )}
          <Button
            size="sm"
            variant="ghost"
            className="text-red-200 hover:text-white text-xs"
            onClick={() => setDismissed(true)}
          >
            Dismiss
          </Button>
        </div>
      </div>

      {!exact && similar.length > 0 && (
        <div className="mt-3 space-y-2">
          {similar.map((c) => (
            <div key={c.coach_id} className="flex items-center justify-between p-3 bg-white/10 rounded-lg">
              <div>
                <span className="font-semibold">{c.name}</span>
                <div className="text-sm text-red-200">{c.school} · {c.position}</div>
              </div>
              <Button
                size="sm"
                onClick={() => claimProfile(c.coach_id)}
                disabled={claiming}
                className="bg-white text-red-700 hover:bg-red-50"
              >
                Claim
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
